import { findLastIndex } from 'lodash';

import {
  HistogramBucket,
  getBucketIndex,
  getDurationFromPercentile,
} from 'src/components/charts/helpers/histogram';
import { assertUnreachable } from 'src/lib/assertUnreachable';

export type HistogramTrace = {
  index: number;
  key?: number;
  durationBucket: number | null;
  sampleTraceId?: string | null;
};

export enum TraceSearchDirection {
  Faster,
  Slower,
  Any,
}

export function findDurationBucketForBucket(
  durationBuckets: Array<number>,
  fromBucketIndex: number,
  searchDirection: TraceSearchDirection,
): HistogramTrace | null {
  const ownIndex = durationBuckets.findIndex(
    (bucketIndex) => bucketIndex === fromBucketIndex,
  );
  if (ownIndex > -1) {
    return {
      index: ownIndex,
      durationBucket: fromBucketIndex,
    };
  }

  let nextOrderedIndex = -1;
  switch (searchDirection) {
    case TraceSearchDirection.Faster: {
      const fasterOrderedIndex = findLastIndex(
        durationBuckets,
        (index) => index < fromBucketIndex,
      );
      if (fasterOrderedIndex > -1) {
        nextOrderedIndex = fasterOrderedIndex;
      }
      break;
    }
    case TraceSearchDirection.Slower: {
      const slowerOrderedIndex = durationBuckets.findIndex(
        (index) => index > fromBucketIndex,
      );
      if (slowerOrderedIndex > -1) {
        nextOrderedIndex = slowerOrderedIndex;
      }
      break;
    }
    case TraceSearchDirection.Any: {
      const fasterOrderedIndex = findLastIndex(
        durationBuckets,
        (index) => index < fromBucketIndex,
      );
      const slowerOrderedIndex = durationBuckets.findIndex(
        (index) => index > fromBucketIndex,
      );

      if (slowerOrderedIndex > -1 && fasterOrderedIndex > -1) {
        const slowDiff = Math.abs(
          // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          durationBuckets[slowerOrderedIndex]! - fromBucketIndex,
        );
        const fastDiff = Math.abs(
          // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          durationBuckets[fasterOrderedIndex]! - fromBucketIndex,
        );

        nextOrderedIndex =
          slowDiff < fastDiff ? slowerOrderedIndex : fasterOrderedIndex;
      } else if (slowerOrderedIndex > -1) {
        nextOrderedIndex = slowerOrderedIndex;
      } else if (fasterOrderedIndex > -1) {
        nextOrderedIndex = fasterOrderedIndex;
      }
      break;
    }
    default:
      assertUnreachable(searchDirection);
      break;
  }

  if (nextOrderedIndex > -1) {
    return {
      index: nextOrderedIndex,
      // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      durationBucket: durationBuckets[nextOrderedIndex]!,
    };
  }

  return null;
}

function getMedianTrace(
  durationBuckets: Array<number>,
  histogramBuckets: ReadonlyArray<HistogramBucket>,
): HistogramTrace | null {
  if (
    !durationBuckets ||
    durationBuckets.length === 0 ||
    !histogramBuckets ||
    histogramBuckets.length === 0
  ) {
    return null;
  }
  const medianDuration = getDurationFromPercentile(histogramBuckets, 0.5);
  if (medianDuration === null) {
    return null;
  }
  const medianBucketIndex = getBucketIndex(medianDuration);
  return findDurationBucketForBucket(
    durationBuckets,
    medianBucketIndex,
    TraceSearchDirection.Any,
  );
}

function getHistogramTraceProjection(
  durationBuckets: Array<number>,
  histogramBuckets: ReadonlyArray<HistogramBucket>,
): HistogramTrace[] {
  const firstBucket = histogramBuckets[0];
  const lastBucket = histogramBuckets[histogramBuckets.length - 1];
  if (
    !durationBuckets ||
    durationBuckets.length === 0 ||
    !firstBucket ||
    !lastBucket
  ) {
    return [];
  }

  const traceBucketMap: HistogramTrace[] = [];
  for (let i = firstBucket.index; i <= lastBucket.index; i++) {
    const index = durationBuckets.findIndex((bucket) => bucket === i);
    traceBucketMap.push({
      key: i,
      index,
      durationBucket: durationBuckets[index] || null,
    });
  }
  return traceBucketMap;
}

export function useHistogramTraceMapper({
  durationBuckets,
  histogramBuckets,
  requestedBucket,
}: {
  durationBuckets: Array<number>;
  histogramBuckets: Array<HistogramBucket>;
  requestedBucket: number | null;
}) {
  const p50TraceInfo = getMedianTrace(durationBuckets, histogramBuckets);
  const histogramTraceProjection = getHistogramTraceProjection(
    durationBuckets,
    histogramBuckets,
  );

  let preferredBucket: HistogramTrace | null = null;
  if (requestedBucket) {
    const requestedBucketIndex = durationBuckets.findIndex(
      (bucket) => bucket === requestedBucket,
    );
    if (requestedBucketIndex > -1) {
      preferredBucket = {
        index: requestedBucketIndex,
        durationBucket: requestedBucket,
      };
    }
  }

  if (!preferredBucket && p50TraceInfo) {
    preferredBucket = p50TraceInfo;
  }

  const nextSlowerTrace =
    preferredBucket && preferredBucket.durationBucket !== null
      ? findDurationBucketForBucket(
          durationBuckets,
          preferredBucket.durationBucket + 1,
          TraceSearchDirection.Slower,
        )
      : null;
  const nextFasterTrace =
    preferredBucket && preferredBucket.durationBucket !== null
      ? findDurationBucketForBucket(
          durationBuckets,
          preferredBucket.durationBucket - 1,
          TraceSearchDirection.Faster,
        )
      : null;

  return {
    nextSlowerTrace,
    nextFasterTrace,
    preferredBucket,
    histogramTraceProjection,
  };
}
